import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTheme, Theme } from '../contexts/ThemeContext';
import { CONSTANTS } from '../constants';
import CloseIcon from './icons/CloseIcon';

// RN-ALIGNED: Clean createStyles function matching React Native pattern
const createStyles = (theme: Theme) => ({
    modalOverlay: {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: theme.colors.modalOverlay,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing.lg,
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: theme.colors.modalBackground,
        borderRadius: theme.radii.lg,
        boxShadow: theme.shadows.modal,
        width: '100%',
        maxWidth: 400,
    },
    modalHeader: {
        display: 'flex',
        flexDirection: 'row' as const,
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing.md,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid' as const,
        borderBottomColor: theme.colors.border,
    },
    modalTitle: {
        fontSize: theme.typography.sizes.lg,
        fontWeight: theme.typography.weights.semiBold,
        color: theme.colors.text,
    },
    chordDisplay: {
        fontSize: theme.typography.sizes.lg,
        fontWeight: theme.typography.weights.bold,
        color: theme.colors.text,
    },
    closeButton: {
        padding: theme.spacing.xs,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: theme.colors.textSecondary,
        fontSize: theme.typography.sizes.xl,
        fontWeight: '300',
        lineHeight: 1,
    },
    inputContainer: {
        padding: theme.spacing.md,
        paddingBottom: theme.spacing.md,
    },
    textInput: {
        backgroundColor: theme.isDark ? theme.colors.surfaceSecondary : theme.colors.surface,
        borderColor: theme.colors.border,
        borderWidth: 1,
        borderStyle: 'solid' as const,
        borderRadius: theme.radii.md,
        padding: theme.spacing.sm,
        fontSize: theme.typography.sizes.md,
        color: theme.colors.text,
        minHeight: 44,
        width: '100%',
        fontFamily: 'inherit',
        outline: 'none',
    },
    counterRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing.xs,
    },
    clearButton: {
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: theme.typography.sizes.xs,
        fontWeight: theme.typography.weights.semiBold,
        color: theme.colors.primary,
    },
    characterCounter: {
        marginLeft: 'auto',
        fontSize: theme.typography.sizes.xs,
        color: theme.colors.textSecondary,
        textAlign: 'right' as const,
    },
    characterCounterError: {
        color: theme.colors.error,
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'row' as const,
        justifyContent: 'space-between',
        gap: theme.spacing.sm,
        paddingTop: theme.spacing.md,
    },
    button: {
        flex: 1,
        paddingTop: theme.spacing.sm,
        paddingBottom: theme.spacing.sm,
        paddingLeft: theme.spacing.lg,
        paddingRight: theme.spacing.lg,
        borderRadius: theme.radii.md,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        border: 'none',
        fontSize: theme.typography.sizes.md,
        fontWeight: theme.typography.weights.semiBold,
        transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
    },
    cancelButton: {
        backgroundColor: theme.colors.surfaceSecondary,
        color: theme.colors.text,
    },
    acceptButton: {
        backgroundColor: theme.colors.primary,
        color: theme.colors.textInverse,
    },
});

interface LyricsInputModalProps {
    visible: boolean;
    currentLyrics?: string | null;
    currentChord?: string;
    onClose: () => void;
    onAccept: (lyrics: string) => void;
}

export const LyricsInputModal: React.FC<LyricsInputModalProps> = ({
    visible,
    currentLyrics = '',
    currentChord,
    onClose,
    onAccept,
}) => {
    const { theme } = useTheme();
    const styles = useMemo(() => createStyles(theme), [theme]);
    const [lyrics, setLyrics] = useState(currentLyrics ?? '');
    const inputRef = useRef<HTMLInputElement>(null);

    // Reset lyrics when modal opens
    useEffect(() => {
        if (visible) {
            setLyrics(currentLyrics ?? '');
        }
    }, [visible, currentLyrics]);

    // Validate and sanitize lyrics input (only allow safe characters for JSON)
    const handleLyricsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const text = e.target.value;
        // Allow: letters (including accented/unicode), numbers, spaces, and safe punctuation
        // Block only problematic characters: double quotes, backslashes, and control characters
        // eslint-disable-next-line no-control-regex -- stripping control characters is the point
        const sanitized = text.replace(/["\\]/g, '').replace(/[\x00-\x1F\x7F]/g, '');
        setLyrics(sanitized);
    };

    const handleAccept = () => {
        onAccept(lyrics);
        onClose();
    };

    // Empties the field only; nothing is saved until Apply, so Cancel still undoes it
    const handleClear = () => {
        setLyrics('');
        inputRef.current?.focus();
    };

    const handleCancel = () => {
        setLyrics(currentLyrics); // Reset to original value
        onClose();
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // Only close if clicking directly on overlay, not on modal content
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    const remainingChars = CONSTANTS.LYRICS_MAX_CHARS - (lyrics?.length ?? 0);
    const isOverLimit = (lyrics?.length ?? 0) > CONSTANTS.LYRICS_MAX_CHARS;

    if (!visible) return null;

    return (
        <div style={styles.modalOverlay} onClick={handleOverlayClick}>
            <div style={styles.modalContent}>
                {/* Header */}
                <div style={styles.modalHeader}>
                    <span style={styles.modalTitle}>
                        Edit lyrics for: {currentChord && (
                            <span style={styles.chordDisplay}>{currentChord}</span>
                        )}
                    </span>
                    <button
                        onClick={handleCancel}
                        style={styles.closeButton}
                        aria-label="Close"
                    >
                        <CloseIcon size={20} color={theme.colors.textSecondary} />
                    </button>
                </div>

                {/* Input Section */}
                <div style={styles.inputContainer}>
                    <input
                        ref={inputRef}
                        type="text"
                        style={styles.textInput}
                        value={lyrics}
                        onChange={handleLyricsChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAccept();
                            }
                        }}
                        placeholder={CONSTANTS.LYRICS_PLACEHOLDER}
                        maxLength={CONSTANTS.LYRICS_MAX_CHARS}
                        autoFocus
                    />
                    <div style={styles.counterRow}>
                        {lyrics.length > 0 && (
                            <button type="button" style={styles.clearButton} onClick={handleClear} aria-label="Clear lyrics">
                                Clear
                            </button>
                        )}
                        <div
                            style={{
                                ...styles.characterCounter,
                                ...(isOverLimit ? styles.characterCounterError : {}),
                            }}
                        >
                            {remainingChars} characters left
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={styles.buttonContainer}>
                        <button
                            style={{ ...styles.button, ...styles.cancelButton }}
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                        <button
                            style={{ ...styles.button, ...styles.acceptButton }}
                            onClick={handleAccept}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LyricsInputModal;
